/**
 * Product reviews section for the product detail page.
 * Displays review stats from product metadata and paginated review list from API.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { JigsawIcon } from '~/local/components/icons';
import { cn } from '~/styling';
import { gradient } from '~/styling/gradients';

import type { IReview, IReviewStats } from '~/lib/reviews.types';

/**
 * Jigsaw-piece rating display.
 */
function JigsawRating({ rating, max = 5, size = 'md' }: { rating: number; max?: number; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'w-3.5 h-3' : 'w-4 h-3.5';
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Fragment key={i}>
          <JigsawIcon
            className={cn(sizeClass, i < Math.round(rating) ? 'text-secondary-1' : 'text-primary-3-10')}
          />
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Rating distribution bars.
 */
function RatingDistribution({ stats }: { stats: IReviewStats }) {
  return (
    <div className="space-y-1">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = stats.ratingDistribution[String(star)] ?? 0;
        const pct = stats.totalCount > 0 ? (count / stats.totalCount) * 100 : 0;
        return (
          <div key={star} className="flex items-center gap-2 text-sm">
            <span className="text-primary-2-80 w-3 text-right">{star}</span>
            <JigsawIcon className="text-secondary-1 h-3 w-3.5" />
            <div className="bg-primary-3-10 h-2 flex-1 overflow-hidden rounded-full">
              <div className="bg-secondary-1 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-primary-2-40 w-6 text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function formatRelativeDate(epochMs: number): string {
  const diff = Date.now() - epochMs;
  const days = Math.floor(diff / 86_400_000);
  const months = Math.floor(days / 30);

  if (days < 1) return 'Today';
  if (days < 30) return `${days}d ago`;
  if (months < 12) return `${months}mo ago`;
  return new Date(epochMs).toLocaleDateString();
}

/**
 * Single review card.
 */
function ReviewCard({ review }: { review: IReview }) {
  return (
    <div className="border-primary-3-10 border-b py-5 last:border-b-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <JigsawRating rating={review.rating} size="sm" />
          <span className="text-primary-2-80 text-sm font-medium">{review.displayName}</span>
        </div>
        <time className="text-primary-2-40 text-xs">{formatRelativeDate(review.createdAt)}</time>
      </div>
      <p className="text-primary-2-80 mt-2 text-sm leading-relaxed">{review.comment}</p>
    </div>
  );
}

interface ProductReviewsProps {
  productId: string;
  stats: IReviewStats | null;
}

export default function ProductReviews({ productId, stats }: ProductReviewsProps) {
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchReviews = useCallback(
    async (cursor?: string) => {
      setIsLoading(true);
      setError(false);

      try {
        const res = await fetch('/api/reviews?' + new URLSearchParams({
          action: 'list',
          productId,
          ...(cursor ? { cursor } : {}),
        }));
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setReviews((prev) => (cursor ? [...prev, ...data.reviews] : data.reviews));
        setNextCursor(data.nextCursor);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [productId],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: only fetch on mount
  useEffect(() => {
    fetchReviews();
  }, [productId]);

  if (!stats || stats.totalCount === 0) return null;

  return (
    <div className={cn(gradient(), 'flex flex-col items-center p-6')}>
      <h2 className="font-heading-condensed text-lg font-medium tracking-tight">Customer Reviews</h2>

      {/* Stats summary */}
      <div className="mt-6 flex w-full flex-col gap-6 sm:flex-row sm:gap-12">
        <div className="flex flex-col items-center sm:items-start">
          <span className="text-primary-2-80 text-4xl font-bold">{stats.averageRating.toFixed(1)}</span>
          <JigsawRating rating={stats.averageRating} />
          <span className="text-primary-2-40 mt-1 text-sm">
            {stats.totalCount} review{stats.totalCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex-1">
          <RatingDistribution stats={stats} />
        </div>
      </div>

      <div className="border-primary-3-10 my-6 w-full border-t" />

      {/* Review list */}
      {error && reviews.length === 0 ? (
        <div className="text-center">
          <p className="text-primary-2-40 text-sm">Couldn't load reviews.</p>
          <button
            type="button"
            onClick={() => fetchReviews()}
            className="text-secondary-1 mt-2 text-sm font-medium"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="w-full">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
          {isLoading && (
            <p className="text-primary-2-40 py-4 text-center text-sm">Loading reviews...</p>
          )}
          {nextCursor && !isLoading && (
            <button
              type="button"
              onClick={() => fetchReviews(nextCursor)}
              className="border-primary-3-10 text-primary-2-80 hover:bg-primary-3-10 mt-4 w-full rounded-full border px-4 py-2 text-sm font-medium"
            >
              Load more reviews
            </button>
          )}
        </div>
      )}
    </div>
  );
}
